import React, { useState } from 'react';
import PropTypes from 'prop-types';

// KT Nov 2025: Category-to-hashtag mappings curated from productive hashtag zone (700-2250 usage_count)
// Each category contains 10 hashtags verified to be in the productive range
// Subtitles show 3 representative examples from the hashtag list
// Due to how we pulled this list together, the keys are V2 hashtags slugs, the values are V1 hashtags ids. The V1
// hashtag IDs should be stable, but the popularity of the hashtags will slowly inflate, causing the highly trafficked
// ones to exceed the spam threshold. So this list will need to be periodically revised. Once every three months
// will probably be sufficient.
const CATEGORY_MAPPINGS = {
  marketing_and_communication: {
    label: 'Marketing & Communication',
    subtitle: 'Marketing, Brand Strategy, Public Relations',
    icon: '📣',
    hashtags: {
      brandstrategy: 144225,
      brandmanagement: 9486,
      brandmarketing: 144134,
      branddevelopment: 146939,
      emailmarketing: 4204,
      onlinemarketing: 142680,
      productmarketing: 146440,
      marketingstrategies: 147276,
      b2bmarketing: 141099,
      influencermarketing: 7190,
    },
  },

  technology_and_innovation: {
    label: 'Technology & Innovation',
    subtitle: 'AI, Cybersecurity, Tech Industry',
    icon: '💻',
    hashtags: {
      aiexpert: 289701,
      generativeai: 209992,
      airesearchers: 343069,
      aidevelopment: 143789,
      aiconsultant: 229247,
      cybersecurityexperts: 319637,
      cyberattack: 2826,
      cyber: 147188,
      techindustry: 147769,
      softwaredevelopment: 3874,
    },
  },

  finance_and_investing: {
    label: 'Finance & Investing',
    subtitle: 'Financial Experts, Investing, Corporate Finance',
    icon: '💰',
    hashtags: {
      financeexperts: 293657,
      investmentmanager: 145787,
      investmentmanagers: 142268,
      investmentbanking: 1023,
      investmentstrategy: 482,
      investmentstrategist: 141042,
      investmentstrategies: 141027,
      corporatefinance: 1973,
      wealthmanagers: 141651,
      investmentadvice: 142242,
      investmentanalysis: 146622,
    },
  },

  business_and_industry_trends: {
    label: 'Business & Industry Trends',
    subtitle: 'Inflation, Supply Chain, Retail & Economic Trends',
    icon: '📊',
    hashtags: {
      economictrends: 185381,
      economicanalysis: 146713,
      economicanalyst: 145294,
      economicanalysts: 1417705,
      economic: 233629,
      economicadvisor: 597735,
      economicadvisors: 720636,
      marketanalyst: 145792,
      retailindustries: 145825,
      businessleaders: 140849,
      realestateeconomics: 27798,
      smallbusinessowners: 141192,
    },
  },

  government_and_politics: {
    label: 'Government & Politics',
    subtitle: 'Legal, Compliance, Politics',
    icon: '🏛️',
    hashtags: {
      legalexperts: 316655,
      legalprofessional: 377941,
      legalprofessionals: 214378,
      legaladvisor: 147201,
      legaladvisors: 331045,
      attorney: 142892,
      attorneys: 147655,
      regulatorycompliance: 1279,
      politicalscientist: 468345,
      publicpolicy: 2780,
    },
  },

  education_and_career: {
    label: 'Education & Career',
    subtitle: 'Education, Teacher, Career Coach',
    icon: '🎓',
    hashtags: {
      careercoach: 144203,
      careercoaching: 144829,
      careercounseling: 51354,
      careercounselor: 148955,
      careerdevelopment: 10570,
      educator: 143680,
      highereducation: 1666,
      professor: 5019,
      coaching: 10748,
      training: 141481,
    },
  },

  health_and_medicine: {
    label: 'Health & Medicine',
    subtitle: 'Healthcare, Physician, Hospital',
    icon: '🏥',
    hashtags: {
      primarycarephysicians: 513613,
      medicalprofessionals: 304375,
      medicalprofessional: 247886,
      medicalexpert: 302205,
      healthcareprofessionals: 294716,
      physicianleader: 172774,
      physiciansgroup: 143766,
      hospitals: 142915,
      clinicalresearch: 28748,
      clinicaldietitian: 191360,
      clinicalnutritionists: 2291209,
      clinicalnutritiondoctor: 1544084,
      clinicalnutritionist: 366859,
    },
  },

  science_and_research: {
    label: 'Science & Research',
    subtitle: 'Biotechnology, Scientific Research, Life Science',
    icon: '🔬',
    hashtags: {
      environmentalscientist: 213540,
      foodscientists: 391973,
      foodscientist: 164345,
      lifesciences: 143988,
      clinicalresearch: 28748,
      biotech: 141470,
      biotechnology: 3293,
    },
  },

  mental_health_and_wellbeing: {
    label: 'Mental Health & Wellbeing',
    subtitle: 'Mental Health, Mindfulness, Psychologist',
    icon: '💗',
    hashtags: {
      mentalhealththerapists: 513615,
      licensedpsychologists: 532415,
      psychologyexperts: 401249,
      psychologyexpert: 318577,
      mentalhealthprofessionals: 318041,
      psychiatrists: 295503,
      mentalhealththerapy: 280004,
      mentalhealthexperts: 297387,
      clinicalpsychology: 5214,
      mindfulness: 2133,
      wellnesscoach: 237988,
      wellnesscoaching: 153765,
      fitnesscoach: 162223,
      fitnesstraining: 144750,
    },
  },

  real_estate_and_market_trends: {
    label: 'Real Estate & Property Markets',
    subtitle: 'Housing, Real Estate, Market Trends',
    icon: '🏘️',
    hashtags: {
      realestateinvestment: 140779,
      realestateinvesting: 889,
      realestatefinance: 140768,
      housingmarket: 142804,
      mortgageloan: 33,
      homebuying: 140740,
      homeownership: 141063,
      propertymanagement: 4013,
      homerenovations: 144564,
    },
  },

  environment_and_sustainability: {
    label: 'Environmental & Energy Solutions',
    subtitle: 'Climate, Sustainability, Policy, Environmental Science',
    icon: '🌱',
    hashtags: {
      climatechange: 266,
      climate: 3005,
      environment: 141106,
      sustainable: 168012,
      ecofriendly: 144044,
      environmentalscientist: 213540,
    },
  },

  design_culture_and_lifestyle: {
    label: 'Design, Culture, & Lifestyle',
    subtitle: 'Art, Entertainment, Travel & Gardening',
    icon: '🎨',
    hashtags: {
      art: 141641,
      popculture: 143892,
      style: 145004,
      designer: 143825,
      designexpert: 180667,
      designtrends: 205327,
      interiordesigning: 1242380,
      interiordecor: 216341,
      fashionindustry: 145586,
      fashionstyling: 225216,
      gardendesign: 68140,
      landscapedesign: 27851,
      homegardening: 204318,
      horticulturepro: 1689847,
      hospitalityindustry: 4167,
      travelindustry: 143121,
      parenting: 149239,
    },
  },
};

function CategoryHashtagSelector(props) {
  const { elementName, className } = props;
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showError, setShowError] = useState(false);

  const toggleCategory = (categoryKey) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryKey)) {
        // Deselecting - always allowed
        return prev.filter((k) => k !== categoryKey);
      }

      // Selecting - check if we've reached the limit
      if (prev.length >= 3) {
        // Already at max, don't allow more selections
        return prev;
      }

      // Add the new selection
      const newSelection = [...prev, categoryKey];

      // Clear error when user makes a selection
      if (newSelection.length > 0) {
        setShowError(false);
      }

      return newSelection;
    });
  };

  const selectedHashtags = selectedCategories.flatMap(
    (catKey) => Object.values(CATEGORY_MAPPINGS[catKey].hashtags),
  );

  const categoryKeys = Object.keys(CATEGORY_MAPPINGS);
  const hasMinimumSelection = selectedCategories.length >= 1;

  // Listen for validation event from form submission
  React.useEffect(() => {
    const handleValidationEvent = () => {
      if (!hasMinimumSelection) {
        setShowError(true);
      }
    };

    const container = document.querySelector('.category-hashtag-selector');
    if (container) {
      container.addEventListener('showValidationError', handleValidationEvent);

      return () => {
        container.removeEventListener('showValidationError', handleValidationEvent);
      };
    }

    return () => {};
  }, [hasMinimumSelection]);

  return (
    <div className={`category-hashtag-selector ${className}`}>
      {/* Friendly helper text at top - not styled as error */}
      <div className="selection-helper-text">
        {selectedCategories.length === 0 && !showError && (
          <span className="text-muted">
            Choose 1-3 categories to get started
          </span>
        )}
        {selectedCategories.length > 0 && selectedCategories.length < 3 && (
          <span className="text-success">
            ✓ {selectedCategories.length} {selectedCategories.length === 1 ? 'category' : 'categories'} selected (you can select up to 3)
          </span>
        )}
        {selectedCategories.length === 3 && (
          <span className="text-success">
            ✓ 3 categories selected (maximum reached)
          </span>
        )}
        {showError && (
          <span className="text-danger fw-bold">
            Please select at least 1 category to continue
          </span>
        )}
      </div>

      <div className="category-grid">
        {categoryKeys.map((catKey) => {
          const category = CATEGORY_MAPPINGS[catKey];
          const isSelected = selectedCategories.includes(catKey);
          const isDisabled = !isSelected && selectedCategories.length >= 3;

          return (
            <button
              key={catKey}
              type="button"
              className={`category-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => toggleCategory(catKey)}
            >
              <div className="category-icon">{category.icon}</div>
              <div className="category-content">
                <div className="category-label">{category.label}</div>
                <div className="category-subtitle">{category.subtitle}</div>
              </div>
              <input
                type="checkbox"
                className="category-checkbox"
                checked={isSelected}
                readOnly
                tabIndex={-1}
              />
            </button>
          );
        })}
      </div>

      {/* Hidden inputs for form submission - maintains compatibility with existing backend */}
      {selectedHashtags.map((tag) => (
        <input
          type="hidden"
          name={elementName}
          value={tag}
          key={tag}
        />
      ))}

      {/* Hidden marker for form validation */}
      <input
        type="hidden"
        className="validation-marker"
        data-has-selection={hasMinimumSelection}
      />
    </div>
  );
}

CategoryHashtagSelector.propTypes = {
  className: PropTypes.string,
  elementName: PropTypes.string.isRequired,
};

CategoryHashtagSelector.defaultProps = {
  className: '',
};

export default CategoryHashtagSelector;
